import { IAttribute } from './../../base/annotation';
import { CopySink } from './copysink';
import { IModel, IViolation } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { FileTypeDataset } from 'app/model/dataset/file-type.dataset';
import { copyBehaviorOptionValues } from '../../../shared/constants/copy-behavior.const';
import { compressionOptionValues } from '../../../shared/components/compression-setting/compression-options';
import { AmazonRedshiftCopySource } from '../copysource/amazon-redshift.copysource';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { isBinaryTypeDataset, isFileTypeDataset, datasetIsFile, isAEPDataset } from 'app/shared/utils/dataset-validation.util';
import { FormatTypeCopySource } from '../copysource/format-type.copysource';
import { LakehouseTableCopySource } from '../copysource/lakehouse-table.copysource';
import { IValidationContext } from 'shell-models-lib';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

@Metamodel.model({
    abstract: true
})
export class FileTypeCopySink extends CopySink {
    public static readonly MODEL: IModel;
    public static readonly COPY_BEHAVIOR: IAttribute;

    @Metamodel.attribute({
        templatable: true,
        association: 'COPY_BEHAVIOR',
        display: stringResources.ModelName_CopyBehavior,
        constraint: {
            validator: FileTypeCopySink.validateCopyBehavior
        }
    })
    public copyBehavior?: string;

    private static async validateCopyBehavior(copySink: FileTypeCopySink, value: string, violation: IViolation, context: IValidationContext): Promise<IViolation[] | undefined> {
        const copyActivity = copySink.owner.named as CopyActivity;
        const sourceDataset = copyActivity.sourceDataset;
        const sinkDataset = copyActivity.sinkDataset as FileTypeDataset;
        if (sourceDataset == null || sinkDataset == null) {
            return undefined;
        }

        // If sink compression type is zipDeflate, copy behavior cannot be set.
        if (sinkDataset && sinkDataset.compression && sinkDataset.compression.compressionType === compressionOptionValues.ZipDeflate && copySink.copyBehavior) {
            return [
                {
                    context: copySink.copyBehavior,
                    message: () => stringResources.CopyBehavior_Must_Be_None_Error
                }
            ];
        }

        const datasetRegistryService = await getServiceInstance<DatasetRegistryService>(context.contextData, 'DatasetRegistryService');
        // binary copy can't set merge files
        // If source is folder, destination is file, copy behavior must be mergefiles
        // we don't validate sink file name expressions here, becouse the value of an expression might be null or ""
        if (!(isBinaryTypeDataset(sourceDataset) || isBinaryTypeDataset(sinkDataset)) && isFileTypeDataset(sourceDataset, { dsSvc: datasetRegistryService }) &&
            (!datasetIsFile(sourceDataset) || (copyActivity.source && copyActivity.source instanceof FormatTypeCopySource && (
                (Metamodel.modelOf(copyActivity.source.storeSettings)?.hasAttribute('wildcardFileName')
                    && Metamodel.modelOf(copyActivity.source.storeSettings).attribute('wildcardFileName').value(copyActivity.source.storeSettings)) ||
                (Metamodel.modelOf(copyActivity.source.storeSettings)?.hasAttribute('wildcardFolderPath')
                    && Metamodel.modelOf(copyActivity.source.storeSettings).attribute('wildcardFolderPath').value(copyActivity.source.storeSettings))
            )))
            && sinkDataset && sinkDataset.fileName && copySink.copyBehavior !== copyBehaviorOptionValues.mergeFiles) {
            return [
                {
                    context: copySink.copyBehavior,
                    message: () => stringResources.CopyBehavior_Must_Be_Mergefiles_Error
                }
            ];
        }

        // If source or sink use binary copy, copy behavior can't be mergefiles
        if (copySink.copyBehavior === copyBehaviorOptionValues.mergeFiles && (isBinaryTypeDataset(sourceDataset) || isBinaryTypeDataset(sinkDataset))) {
            return !isAEPDataset(sourceDataset) ? [
                {
                    context: copySink.copyBehavior,
                    message: () => stringResources.CopyBehavior_Cannot_Be_Mergefiles_When_Use_BinaryCopy_Error
                }
            ] : [
                {
                    context: copySink.copyBehavior,
                    message: () => stringResources.AEP_CopyBehavior_ErrorMessage // customize error message when source is aep
                }
            ];
        }
        // when copy from table to file and filename is set, and staging or unload is used, copy behavior must be merging files
        // we don't validate sink file name expressions here, becouse the value of an expression might be null or ""
        if ((copyActivity.enableStaging
            || (copyActivity.source instanceof AmazonRedshiftCopySource && copyActivity.source.enableRedshiftUnloadSettings))
            && datasetRegistryService.getDatasetConfiguration(Metamodel.modelOf(sourceDataset).name).datasetKind !== 'file'
            && sinkDataset && sinkDataset.fileName && copySink.copyBehavior !== copyBehaviorOptionValues.mergeFiles) {
            return [
                {
                    context: copySink.copyBehavior,
                    message: () => copyActivity.enableStaging
                        ? stringResources.CopyBehavior_Must_Be_Mergefiles_Error_Staging
                        : stringResources.CopyBehavior_Must_Be_Mergefiles_Error_Unload
                }
            ];
        }

        // when copy from table to file, and staging is not used and unload is not used, copy behavior can't be merging files
        if (!copyActivity.enableStaging && !ValueAdapter.isTemplated(copyActivity, CopyActivity.ENABLE_STAGING)
            && (!(copyActivity.source instanceof AmazonRedshiftCopySource)
                || !copyActivity.source.enableRedshiftUnloadSettings)
            && !(copyActivity.source instanceof LakehouseTableCopySource)
            && datasetRegistryService.getDatasetConfiguration(Metamodel.modelOf(sourceDataset).name).datasetKind !== 'file'
            && copySink.copyBehavior === copyBehaviorOptionValues.mergeFiles) {
            return [
                {
                    context: copySink.copyBehavior,
                    message: () => stringResources.CopyBehavior_Cannot_Be_Mergefiles_Error_Table
                }
            ];
        }

        return undefined;
    }
}
